import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { checkAuthThrottle, recordAuthAttempt } from "@/lib/auth-throttle.functions";
import { confirmSignupCode, requestSignupCode } from "@/lib/signup-otp.functions";

import {
  clearLocalFailures,
  formatWait,
  localCooldown,
  recordLocalFailure,
  type AuthAction,
} from "@/lib/auth-throttle";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Team Sign In | Finendra" },
      {
        name: "description",
        content: "Secure sign-in for the Finendra team to manage client inquiries.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

const credentials = z.object({
  email: z.string().trim().email("Enter a valid email address").max(255),
  password: z.string().min(8, "Password must be at least 8 characters").max(72),
});

const emailOnly = z.object({
  email: z.string().trim().email("Enter a valid email address").max(255),
});

type Mode = "signin" | "signup" | "reset" | "verify";

const MODE_ACTION: Record<Mode, AuthAction> = {
  signin: "signin",
  signup: "signup",
  reset: "reset",
  verify: "signup",
};

const HEADINGS: Record<Mode, string> = {
  signin: "Team sign in",
  signup: "Create team account",
  reset: "Reset your password",
  verify: "Verify your email",
};

function AuthPage() {
  const navigate = useNavigate();
  const check = useServerFn(checkAuthThrottle);
  const record = useServerFn(recordAuthAttempt);
  const requestCode = useServerFn(requestSignupCode);
  const confirmCode = useServerFn(confirmSignupCode);

  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [maxAttempts, setMaxAttempts] = useState(5);
  const [lockedOut, setLockedOut] = useState(false);
  const [codeExpiresAt, setCodeExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [pendingEmail, setPendingEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const action = MODE_ACTION[mode];

  useEffect(() => {
    if (mode !== "verify" || !codeExpiresAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [mode, codeExpiresAt]);

  const secondsLeft = codeExpiresAt ? Math.max(0, Math.round((codeExpiresAt - now) / 1000)) : null;
  const expired = secondsLeft !== null && secondsLeft === 0;
  const formatCountdown = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  /** Inline field-level validation for the OTP input. */
  function validateCode(value: string): string | null {
    if (value.length === 0) return "Enter the 6-digit code from your email.";
    if (value.length < 6)
      return `${6 - value.length} digit${6 - value.length === 1 ? "" : "s"} to go.`;
    if (!/^\d{6}$/.test(value)) return "The code contains digits only.";
    return null;
  }

  function onCodeChange(raw: string) {
    const next = raw.replace(/\D/g, "").slice(0, 6);
    setCode(next);
    setError(null);
    if (codeTouched) setCodeError(validateCode(next));
  }

  async function onVerify(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setCodeTouched(true);

    const fieldError = validateCode(code);
    setCodeError(fieldError);
    if (fieldError) return;
    if (lockedOut || expired) return;

    setBusy(true);
    try {
      const result = await confirmCode({ data: { email: pendingEmail, code: code.trim() } });
      if (!result.ok) {
        setMaxAttempts(result.maxAttempts);
        setAttemptsRemaining(result.attemptsRemaining);
        if (result.reason === "locked") {
          setLockedOut(true);
          setCodeError(null);
          setError(
            `Too many incorrect codes — this code is now locked. Request a new one to continue.`,
          );
        } else if (result.reason === "expired") {
          setCodeExpiresAt(Date.now());
          setCodeError(null);
          setError("That code has expired. Request a new one.");
        } else {
          setCodeError(
            `That code is not correct. ${result.attemptsRemaining} of ${result.maxAttempts} attempt${
              result.attemptsRemaining === 1 ? "" : "s"
            } remaining.`,
          );
        }
        return;
      }
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: pendingEmail,
        password,
      });
      if (signInError) {
        setNotice("Email verified. You can now sign in.");
        setMode("signin");
        return;
      }
      await navigate({ to: "/dashboard", replace: true });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function resendCode() {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await requestCode({ data: { email: pendingEmail, password } });
      setCode("");
      setCodeTouched(false);
      setCodeError(null);
      setLockedOut(false);
      setAttemptsRemaining(null);
      setCodeExpiresAt(Date.now() + 10 * 60_000);
      setNow(Date.now());
      setNotice(`A new code is on its way to ${pendingEmail}. It expires in 10 minutes.`);
    } catch {
      setError("Could not resend the code. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    const parsed =
      mode === "reset"
        ? emailOnly.safeParse({ email })
        : credentials.safeParse({ email, password });

    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid details");
      return;
    }

    // Layer 1 — immediate client-side backoff/lockout.
    const wait = localCooldown(action);
    if (wait > 0) {
      setError(`Too many attempts. Try again in ${formatWait(wait)}.`);
      return;
    }

    setBusy(true);
    try {
      // Layer 2 — server-tracked lockout by email and network address.
      const gate = await check({ data: { action, email: parsed.data.email } });
      if (!gate.allowed) {
        recordLocalFailure(action);
        setError(`Too many attempts. Try again in ${formatWait(gate.retryAfter)}.`);
        return;
      }

      if (mode === "signin") {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: parsed.data.email,
          password,
        });
        await record({ data: { action, email: parsed.data.email, success: !signInError } });
        if (signInError) {
          recordLocalFailure(action);
          setError("Incorrect email or password.");
          return;
        }
        clearLocalFailures(action);
        await navigate({ to: "/dashboard", replace: true });
      } else if (mode === "signup") {
        const result = await requestCode({
          data: { email: parsed.data.email, password },
        });
        await record({ data: { action, email: parsed.data.email, success: true } });
        clearLocalFailures(action);
        if (result.status === "already_registered") {
          setNotice("That address already has an account — sign in instead.");
          return;
        }
        setPendingEmail(parsed.data.email);
        setCode("");
        setCodeTouched(false);
        setCodeError(null);
        setLockedOut(false);
        setAttemptsRemaining(null);
        setCodeExpiresAt(Date.now() + 10 * 60_000);
        setNow(Date.now());
        setMode("verify");
        setNotice(`We emailed a 6-digit code to ${parsed.data.email}. It expires in 10 minutes.`);
      } else {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(parsed.data.email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        await record({ data: { action, email: parsed.data.email, success: !resetError } });
        if (resetError) {
          recordLocalFailure(action);
        }
        // Never reveal whether the address exists.
        setNotice("If that address has an account, a reset link is on its way.");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  function switchTo(next: Mode) {
    setMode(next);
    setError(null);
    setNotice(null);
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-6 py-24">
      <div className="w-full max-w-md rounded-3xl border border-brand-navy/10 bg-background p-10">
        <h1 className="font-display text-3xl font-bold text-brand-navy">{HEADINGS[mode]}</h1>
        <p className="mt-3 text-sm text-brand-navy/60">
          {mode === "reset"
            ? "We'll email you a secure link to choose a new password."
            : mode === "verify"
              ? `Enter the 6-digit code we sent to ${pendingEmail}.`
              : "Access to the Finendra inquiry inbox is restricted to authorised staff."}
        </p>

        {mode === "verify" ? (
          <form onSubmit={onVerify} className="mt-8 space-y-5">
            <div>
              <div className="flex items-baseline justify-between gap-3">
                <label
                  htmlFor="otp"
                  className="text-xs font-bold uppercase tracking-widest text-brand-navy"
                >
                  Verification code
                </label>
                {secondsLeft !== null && (
                  <span
                    className={`text-xs font-semibold tabular-nums ${
                      expired ? "text-destructive" : "text-brand-navy/50"
                    }`}
                  >
                    {expired ? "Code expired" : `Expires in ${formatCountdown(secondsLeft)}`}
                  </span>
                )}
              </div>
              <input
                id="otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                disabled={lockedOut || expired}
                aria-invalid={Boolean(codeError) || lockedOut || expired}
                aria-describedby="otp-help"
                onBlur={() => {
                  setCodeTouched(true);
                  setCodeError(validateCode(code));
                }}
                onChange={(e) => onCodeChange(e.target.value)}
                className={`mt-2 w-full rounded-xl border bg-background px-4 py-3 text-center text-2xl tracking-[0.5em] text-brand-navy outline-none disabled:opacity-60 ${
                  codeError || lockedOut || expired
                    ? "border-destructive focus:border-destructive"
                    : "border-brand-navy/15 focus:border-brand-accent"
                }`}
                required
              />
              <p
                id="otp-help"
                role={codeError ? "alert" : undefined}
                className={`mt-2 text-sm ${
                  codeError ? "font-medium text-destructive" : "text-brand-navy/50"
                }`}
              >
                {codeError ??
                  (attemptsRemaining !== null && attemptsRemaining > 0
                    ? `${attemptsRemaining} of ${maxAttempts} attempts remaining.`
                    : "Six digits, no spaces. Check your inbox and spam folder.")}
              </p>
            </div>

            {(lockedOut || expired) && (
              <div
                role="alert"
                className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
              >
                <p className="font-semibold">
                  {lockedOut ? "Verification locked" : "Verification code expired"}
                </p>
                <p className="mt-1 text-destructive/80">
                  {lockedOut
                    ? `You used all ${maxAttempts} attempts for this code. Request a fresh code to try again.`
                    : "Codes are valid for 10 minutes. Request a fresh code to continue."}
                </p>
              </div>
            )}

            {error && !lockedOut && !expired && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error}
              </p>
            )}
            {notice && <p className="text-sm font-medium text-brand-accent">{notice}</p>}

            <button
              type="submit"
              disabled={busy || lockedOut || expired || code.length !== 6}
              className="w-full rounded-xl bg-brand-navy px-6 py-4 text-sm font-bold text-background transition-[background-color,transform] duration-150 ease-out hover:bg-brand-accent active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none disabled:opacity-60 disabled:transform-none"
            >
              {busy ? "Please wait…" : "Verify and continue"}
            </button>

            <div className="space-y-2 text-center">
              <button
                type="button"
                onClick={resendCode}
                disabled={busy}
                className="block w-full text-sm text-brand-navy/60 hover:text-brand-accent"
              >
                Resend code
              </button>
              <button
                type="button"
                onClick={() => switchTo("signup")}
                className="block w-full text-sm text-brand-navy/60 hover:text-brand-accent"
              >
                Use a different email
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label
                htmlFor="email"
                className="text-xs font-bold uppercase tracking-widest text-brand-navy"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full rounded-xl border border-brand-navy/15 bg-background px-4 py-3 text-brand-navy outline-none focus:border-brand-accent"
                required
              />
            </div>

            {mode !== "reset" && (
              <div>
                <label
                  htmlFor="password"
                  className="text-xs font-bold uppercase tracking-widest text-brand-navy"
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete={mode === "signin" ? "current-password" : "new-password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-brand-navy/15 bg-background px-4 py-3 text-brand-navy outline-none focus:border-brand-accent"
                  required
                />
              </div>
            )}

            {error && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {error}
              </p>
            )}
            {notice && <p className="text-sm font-medium text-brand-accent">{notice}</p>}

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-brand-navy px-6 py-4 text-sm font-bold text-background transition-[background-color,transform] duration-150 ease-out hover:bg-brand-accent active:scale-[0.985] active:duration-75 motion-reduce:transform-none select-none disabled:opacity-60 disabled:transform-none"
            >
              {busy
                ? "Please wait…"
                : mode === "signin"
                  ? "Sign in"
                  : mode === "signup"
                    ? "Create account"
                    : "Send reset link"}
            </button>
          </form>
        )}

        {mode !== "verify" && (
          <div className="mt-6 space-y-2 text-center">
            <button
              type="button"
              onClick={() => switchTo(mode === "signup" ? "signin" : "signup")}
              className="block w-full text-sm text-brand-navy/60 hover:text-brand-accent"
            >
              {mode === "signup"
                ? "Already have an account? Sign in"
                : "Need an account? Create one"}
            </button>
            <button
              type="button"
              onClick={() => switchTo(mode === "reset" ? "signin" : "reset")}
              className="block w-full text-sm text-brand-navy/60 hover:text-brand-accent"
            >
              {mode === "reset" ? "Back to sign in" : "Forgot your password?"}
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
